import appConfig from "@/configs/app-config";
import { dateFormats } from "@/utils/dateFormatter";
import { TrackingEvent } from "../../constants/events";
import { pushToDataLayer } from "../../providers/google/utils";
import { buildFerryEcommerceItems, chunkItemsForGa } from "./utils";

/**
 * Google Analytics - Ferry Provider
 * Ferry kategorisi için Google Analytics event'lerini yönetir
 */
export class FerryGoogleProvider {
  track(event, data) {
    switch (event) {
      case TrackingEvent.VIRTUAL_PV:
        this.trackVirtualPv(data);
        break;
      case TrackingEvent.CTA_CLICK:
        this.trackCtaClick(data);
        break;
      case TrackingEvent.CHECK_RESERVATION:
        this.trackCheckReservation(data);
        break;
      case TrackingEvent.CALL_TO_CANCEL_RESERVATION:
        this.trackCallToCancelReservation(data);
        break;
      case TrackingEvent.SUPPORT_TO_CANCEL_RESERVATION:
        this.trackSupportToCancelReservation(data);
        break;
      case TrackingEvent.SEARCH_RESULT:
        this.trackSearchResult(data);
        break;
      case TrackingEvent.NO_SEARCH_RESULT:
        this.trackNoSearchResult(data);
        break;
      case TrackingEvent.FILTER_APPLIED:
        this.trackFilterApplied(data);
        break;
      case TrackingEvent.PURCHASE:
        this.trackPurchase(data);
        break;
      case TrackingEvent.BEGIN_CHECKOUT:
        this.trackBeginCheckout(data);
        break;
      case TrackingEvent.SELECT_ITEM:
        this.trackSelectItem(data);
        break;
      case TrackingEvent.VIEW_ITEM_LIST:
        this.trackViewItemList(data);
        break;
      case TrackingEvent.VIEW_ITEM:
        this.trackViewItem(data);
        break;
      case TrackingEvent.ADD_TO_CART:
        this.trackAddToCart(data);
        break;
      case TrackingEvent.SELECT_COUPON:
      case TrackingEvent.VIEW_COUPON:
      case TrackingEvent.COUPON_APPLIED:
        this.trackCouponEvent(event, data);
        break;
      case TrackingEvent.SEARCH:
        this.trackSearch(data);
        break;
      case TrackingEvent.SHOWN_ERROR:
        this.trackShownError(data);
        break;
      default:
        this.trackGenericEvent(event, data);
    }
  }

  /**
   * virtual_pv
   * Ferry search-list (listing) sayfası için GA4 CSV'deki virtual_pv şemasına göre payload.
   */
  trackVirtualPv(data) {
    pushToDataLayer(TrackingEvent.VIRTUAL_PV, {
      event: data.event || "virtual_pv",
      environment: data.environment || "prod",
      virtual_page_url: data.virtual_page_url, // Örn: "/ferry/search-list"
      page_type: data.page_type || "ferrysearchlist",

      ferry_destination: data.ferry_destination,
      language: data.language,
      trip_type: data.trip_type, // OW / RT
      departure_city: data.departure_city,
      arrival_city: data.arrival_city,
      location_type: data.location_type, // domestic / international
      provider: data.provider,
      num_adults: data.num_adults,
      num_children: data.num_children,
      num_total: data.num_total,

      turna_session_id: data.turna_session_id,
      turna_device_id: data.turna_device_id,
      version: appConfig.WEBSITE_VERSION,
      user_id: data.user_id.toString() ?? "",
      user_hashed_email: data.user_hashed_email,
      user_hashed_phone: data.user_hashed_phone,
      user_type: data.user_type,
      guest_user_hashed_mail: data.guest_user_hashed_mail,
      guest_user_hashed_phone: data.guest_user_hashed_phone,
      // user_mail: data.user_mail,
      // user_phone: data.user_phone,
      user_turna_points: data.user_turna_points,
    });
  }

  trackCtaClick(data) {
    pushToDataLayer(TrackingEvent.CTA_CLICK, {
      event: "custom_event",
      event_category: "click",
      event_action: "cta_click",
      cta_button: data.cta_button, // butonun ismi basılacak
      cta_location: "body", // sayfada bulunduğu konum
      cta_category: data.cta_category, //başlığın ismi
      cta_language: data.cta_language,
    });
  }

  trackCheckReservation(data) {
    pushToDataLayer(TrackingEvent.CHECK_RESERVATION, {
      event: "custom_event",
      event_category: "check_reservation",
      event_action: "check_reservation",
      pnr_number: data.pnr_number,
      reservation_category: "ferry",
    });
  }

  trackCallToCancelReservation(data) {
    pushToDataLayer(TrackingEvent.CALL_TO_CANCEL_RESERVATION, {
      event: "custom_event",
      event_category: "custom_event",
      event_action: "call_to_cancel_reservation",
      pnr_number: data.pnr_number,
      reservation_category: "ferry",
    });
  }

  trackSupportToCancelReservation(data) {
    pushToDataLayer(TrackingEvent.SUPPORT_TO_CANCEL_RESERVATION, {
      event: "custom_event",
      event_category: "custom_event",
      event_action: "support_to_cancel_reservation",
      pnr_number: data.pnr_number,
      productType: data.productType,
      reservation_category: "ferry",
    });
  }

  trackSearchResult(data) {
    pushToDataLayer(TrackingEvent.SEARCH_RESULT, {
      event: "custom_event",
      event_category: "search",
      event_action: "search_result",
      search_category: "ferry",
      search_destination: data.search_destination,
      trip_type: data.trip_type,
      start_date: data.start_date,
      end_date: data.end_date,
      origin: data.origin,
      destination: data.destination,
      passengers_total: data.passengers_total,
      with_vehicle: data.with_vehicle,
      search_result_number: data.search_result_number,
    });
  }

  trackNoSearchResult(data) {
    pushToDataLayer(TrackingEvent.NO_SEARCH_RESULT, {
      event: "custom_event",
      event_category: "search",
      event_action: "no_search_result",
      search_category: "ferry",
      search_destination: data.search_destination,
      trip_type: data.trip_type,
      start_date: data.start_date,
      end_date: data.end_date,
      origin: data.origin,
      destination: data.destination,
      passengers_total: data.passengers_total,
      with_vehicle: data.with_vehicle,
      search_result_number: data.search_result_number,
    });
  }

  trackSearch(data) {
    pushToDataLayer(TrackingEvent.SEARCH, {
      event: "custom_event",
      event_category: "search",
      event_action: "search",
      search_category: "ferry",
      search_term: data.search_term,
    });
  }

  trackFilterApplied(data) {
    pushToDataLayer(TrackingEvent.FILTER_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "filter_applied",
      filter_product_category: "ferry",
      filter_category: data?.filter_category,
      filter_subcategory: data?.filter_subcategory,
      sorting_type: data?.sorting_type,
    });
  }

  trackViewItemList(data) {
    const trips = Array.isArray(data?.trips) ? data.trips : [];
    if (!trips.length) return;

    const itemListId = data?.item_list_id || "ferry_search";
    const itemListName = data?.item_list_name || "Ferry Search";
    const tripType = data?.trip_type || (data?.returnDate ? "RT" : "OW");
    const travelType = data?.travelType || "domestic";

    // Tüm items'ı build et
    const items = buildFerryEcommerceItems({
      trips,
      summaryData: data?.summaryData || {},
      item_list_id: itemListId,
      item_list_name: itemListName,
      withVehicle: data?.withVehicle || false,
      travelType: travelType,
    });

    if (!items.length) return;

    // 8'erli gruplara böl
    const chunks = chunkItemsForGa(items, 8);

    // Önceki ecommerce datasını temizle
    pushToDataLayer({ ecommerce: null });

    chunks.forEach((chunk) => {
      if (!chunk || !chunk.length) return;

      // Her chunk için ayrı view_item_list eventi gönder
      pushToDataLayer(TrackingEvent.VIEW_ITEM_LIST, {
        event: "ecom_event",
        event_category: "ecommerce",
        event_action: "view_item_list",
        trip_type: tripType,
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: chunk,
        },
      });
    });
  }

  trackSelectItem(data) {
    const trip = data?.trip;
    if (!trip) return;
    const itemListId = data?.item_list_id || "ferry_search";
    const itemListName = data?.item_list_name || "Ferry Search";
    const tripType = data?.trip_type || (data?.returnDate ? "RT" : "OW");
    const travelType = data?.travelType || "domestic";

    const ticket_type = data?.ticket_type;
    const tripWithTicketType = ticket_type ? { ...trip, ticket_type } : trip;

    // Tek trip için item üret
    const items = buildFerryEcommerceItems({
      trips: [tripWithTicketType],
      summaryData: data?.summaryData || {},
      item_list_id: itemListId,
      item_list_name: itemListName,
      withVehicle: data?.withVehicle || false,
      travelType,
    });

    if (!Array.isArray(items) || items.length === 0) return;

    const baseItem = { ...items[0], index: 1 };

    const selectedPackage = data?.selectedPackage;
    const finalItem = {
      ...baseItem,
      // Paket verisi varsa fiyatı paket fiyatı ile override et
      price:
        selectedPackage?.Price != null ? selectedPackage.Price : baseItem.price,
    };

    // Önceki ecommerce datasını temizle
    pushToDataLayer({ ecommerce: null });

    // Tek item'lı select_item eventi
    pushToDataLayer(TrackingEvent.SELECT_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "select_item",
      trip_type: tripType,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: [finalItem],
      },
    });
  }

  trackBeginCheckout(data) {
    const products = Array.isArray(data?.products) ? data.products : [];
    if (!products.length) return;

    const firstProduct = products[0];
    const legs = Array.isArray(firstProduct?.Legs) ? firstProduct.Legs : [];
    if (!legs.length) return;

    const isDomestic =
      typeof firstProduct?.IsDomestic === "boolean"
        ? firstProduct.IsDomestic
        : !!data?.isDomestic;
    const hasVehicle = !!data?.hasVehicle;

    const priceSummary = firstProduct?.PriceSummary || {};
    const currency = data?.currency || priceSummary?.Currency || "TRY";
    const totalValue =
      data?.value ?? priceSummary?.TotalFareBeforeDiscount ?? 0;
    // Kupon ve indiri m i bilgisi ürün PriceSummary'inden gelir
    const coupon = priceSummary?.AppliedCampaignName || data?.coupon || "";
    const totalDiscount = Number(priceSummary?.Discount) || 0;

    const numLegs = legs.length;
    const perLegPrice = numLegs > 0 ? totalValue / numLegs : totalValue;
    const perLegDiscount =
      numLegs > 0 ? totalDiscount / numLegs : totalDiscount;

    const items = legs.map((leg, idx) => {
      const segment = Array.isArray(leg?.Segments) ? leg.Segments[0] || {} : {};

      const originName = (segment.Origin || "").split(",")[0] || "";
      const destinationName = (segment.Destination || "").split(",")[0] || "";

      const startDate =
        typeof segment.DepartureDate === "string"
          ? segment.DepartureDate.slice(0, 10)
          : "";
      const endDate =
        typeof segment.ArrivalDate === "string"
          ? segment.ArrivalDate.slice(0, 10)
          : "";

      const ticket_type = idx === 0 ? "outbound" : "inbound";
      return {
        item_id: `${originName}-${destinationName}`,
        item_name: "ferry",
        cross_product_promotion: "",
        affiliation: "turna.com",
        currency,
        item_brand: segment.OperatingCompany || null,
        index: idx + 1,
        coupon,
        discount: perLegDiscount,
        start_date: startDate,
        end_date: endDate,
        origin: originName,
        destination: destinationName,
        item_list_id: "ferry_search",
        item_list_name: "Ferry Search",
        item_category: "ferry",
        item_category2: isDomestic ? "domestic" : "international",
        item_category3: hasVehicle ? "with_car" : "without_car",
        item_category4: "economy",
        price: perLegPrice,
        quantity: 1,
        ticket_type,
      };
    });

    if (!items.length) return;

    const firstSeg =
      Array.isArray(legs[0]?.Segments) && legs[0].Segments.length > 0
        ? legs[0].Segments[0]
        : {};
    const lastLeg = legs[legs.length - 1];
    const lastSeg =
      Array.isArray(lastLeg?.Segments) && lastLeg.Segments.length > 0
        ? lastLeg.Segments[0]
        : {};

    const startDate =
      typeof firstSeg.DepartureDate === "string"
        ? firstSeg.DepartureDate.slice(0, 10)
        : "";
    const endDate =
      typeof lastSeg.ArrivalDate === "string"
        ? lastSeg.ArrivalDate.slice(0, 10)
        : "";

    const rtFlightId = items.map((it) => it.item_id).join(",");

    // Önceki ecommerce datasını temizle
    pushToDataLayer({ ecommerce: null });

    pushToDataLayer(TrackingEvent.BEGIN_CHECKOUT, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "begin_checkout",
      trip_type: legs.length > 1 ? "RT" : "OW",
      rt_flight_id: rtFlightId,
      start_to_end_date: `${dateFormats.iso(startDate)} , ${dateFormats.iso(endDate)}`,
      ecommerce: {
        currency,
        value: totalValue,
        ...(coupon ? { coupon } : {}),
        ...(data?.reservationId
          ? { basket_id: String(data.reservationId) }
          : {}),
        items,
      },
    });
  }

  trackViewItem(data) {
    const products = Array.isArray(data?.products) ? data.products : [];
    if (!products.length) return;

    const firstProduct = products[0];
    const legs = Array.isArray(firstProduct?.Legs) ? firstProduct.Legs : [];
    if (!legs.length) return;

    const isDomestic =
      typeof firstProduct?.IsDomestic === "boolean"
        ? firstProduct.IsDomestic
        : !!data?.isDomestic;
    const hasVehicle = !!data?.hasVehicle;

    const priceSummary = data?.priceSummary || firstProduct?.PriceSummary || {};

    const currency = data?.currency || priceSummary?.Currency || "TRY";
    const totalValue =
      data?.value ?? priceSummary?.TotalFareBeforeDiscount ?? 0;

    const coupon = data?.coupon ?? priceSummary?.AppliedCampaignName ?? null;
    const totalDiscount = Number(priceSummary?.Discount) || 0;

    const numLegs = legs.length;
    const perLegPrice = numLegs > 0 ? totalValue / numLegs : totalValue;
    const perLegDiscount =
      numLegs > 0 ? totalDiscount / numLegs : totalDiscount;

    const crossProductPromotion =
      data?.cross_product_promotion ??
      firstProduct?.CrossProductPromotion ??
      "";

    const items = legs.map((leg, idx) => {
      const segment = Array.isArray(leg?.Segments) ? leg.Segments[0] || {} : {};

      const originName = (segment.Origin || "").split(",")[0] || "";
      const destinationName = (segment.Destination || "").split(",")[0] || "";

      const startDate =
        typeof segment.DepartureDate === "string"
          ? segment.DepartureDate.slice(0, 10)
          : "";
      const endDate =
        typeof segment.ArrivalDate === "string"
          ? segment.ArrivalDate.slice(0, 10)
          : "";

      const ticket_type = idx === 0 ? "outbound" : "inbound";

      return {
        item_id: `${originName}-${destinationName}`,
        item_name: "ferry",
        cross_product_promotion: crossProductPromotion,
        affiliation: "turna.com",
        currency,
        item_brand: segment.OperatingCompany || null,
        ticket_type,
        index: idx + 1,
        coupon,
        discount: perLegDiscount,
        start_date: startDate,
        end_date: endDate,
        origin: originName,
        destination: destinationName,
        item_list_id: "ferry_search",
        item_list_name: "Ferry Search",
        item_category: "ferry",
        item_category2: isDomestic ? "domestic" : "international",
        item_category3: hasVehicle ? "with_car" : "without_car",
        item_category4: "economy",
        price: perLegPrice,
        quantity: 1,
      };
    });

    if (!items.length) return;

    const basketId =
      data?.basket_id ??
      data?.basketId ??
      data?.BasketId ??
      data?.reservationId ??
      null;
    const hasBasketId = basketId != null && String(basketId).trim() !== "";

    // Önceki ecommerce datasını temizle
    pushToDataLayer({ ecommerce: null });

    pushToDataLayer(TrackingEvent.VIEW_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item",
      trip_type: legs.length > 1 ? "RT" : "OW",
      ecommerce: {
        currency,
        value: totalValue,
        ...(hasBasketId ? { basket_id: String(basketId) } : {}),
        items,
      },
    });
  }

  trackAddToCart(data) {
    const products = Array.isArray(data?.products) ? data.products : [];
    if (!products.length) return;

    const firstProduct = products[0];
    const legs = Array.isArray(firstProduct?.Legs) ? firstProduct.Legs : [];
    if (!legs.length) return;

    const isDomestic =
      typeof firstProduct?.IsDomestic === "boolean"
        ? firstProduct.IsDomestic
        : !!data?.isDomestic;
    const hasVehicle = !!data?.hasVehicle;

    const priceSummary = data?.priceSummary || firstProduct?.PriceSummary || {};

    const currency = data?.currency || priceSummary?.Currency || "TRY";
    const totalValue =
      data?.value ?? priceSummary?.TotalFareBeforeDiscount ?? 0;

    const coupon = data?.coupon ?? priceSummary?.AppliedCampaignName ?? null;
    const totalDiscount = Number(priceSummary?.Discount) || 0;

    const numLegs = legs.length;
    const perLegPrice = numLegs > 0 ? totalValue / numLegs : totalValue;
    const perLegDiscount =
      numLegs > 0 ? totalDiscount / numLegs : totalDiscount;

    const crossProductPromotion =
      data?.cross_product_promotion ??
      firstProduct?.CrossProductPromotion ??
      "";

    const items = legs.map((leg, idx) => {
      const segment = Array.isArray(leg?.Segments) ? leg.Segments[0] || {} : {};

      const originName = (segment.Origin || "").split(",")[0] || "";
      const destinationName = (segment.Destination || "").split(",")[0] || "";

      const startDate =
        typeof segment.DepartureDate === "string"
          ? segment.DepartureDate.slice(0, 10)
          : "";
      const endDate =
        typeof segment.ArrivalDate === "string"
          ? segment.ArrivalDate.slice(0, 10)
          : "";

      const ticket_type = idx === 0 ? "outbound" : "inbound";

      return {
        item_id: `${originName}-${destinationName}`,
        item_name: "ferry",
        cross_product_promotion: crossProductPromotion,
        affiliation: "turna.com",
        currency,
        item_brand: segment.OperatingCompany || null,
        ticket_type,
        index: idx + 1,
        coupon,
        discount: perLegDiscount,
        start_date: startDate,
        end_date: endDate,
        origin: originName,
        destination: destinationName,
        item_list_id: "ferry_search",
        item_list_name: "Ferry Search",
        item_category: "ferry",
        item_category2: isDomestic ? "domestic" : "international",
        item_category3: hasVehicle ? "with_car" : "without_car",
        item_category4: "economy",
        price: perLegPrice,
        quantity: 1,
      };
    });

    if (!items.length) return;

    const basketId =
      data?.basket_id ??
      data?.basketId ??
      data?.BasketId ??
      data?.reservationId ??
      null;
    const hasBasketId = basketId != null && String(basketId).trim() !== "";

    // Önceki ecommerce objesini temizle
    pushToDataLayer({ ecommerce: null });

    pushToDataLayer(TrackingEvent.ADD_TO_CART, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_to_cart",
      trip_type: legs.length > 1 ? "RT" : "OW",
      ecommerce: {
        currency,
        value: totalValue,
        ...(hasBasketId ? { basket_id: String(basketId) } : {}),
        items,
      },
    });
  }

  trackPurchase(data) {
    const products = Array.isArray(data?.products) ? data.products : [];
    if (!products.length) return;

    const firstProduct = products[0];
    const legs = Array.isArray(firstProduct?.Legs) ? firstProduct.Legs : [];
    if (!legs.length) return;
    const isDomestic =
      typeof firstProduct?.IsDomestic === "boolean"
        ? firstProduct.IsDomestic
        : !!data?.isDomestic;
    const hasVehicle = !!data?.hasVehicle;

    const currency =
      data?.currency ||
      firstProduct?.PriceSummary?.Currency ||
      data?.CustomerCurrency ||
      "TRY";
    const totalValue = firstProduct?.PriceSummary?.TotalFareBeforeDiscount ?? 0;

    const coupon = firstProduct?.PriceSummary?.AppliedCampaignName || null;
    const discount = Number(firstProduct?.PriceSummary?.Discount) || 0;
    const numLegs = legs.length || 1;
    const perLegPrice = totalValue / numLegs;
    const perLegDiscount = discount / numLegs;

    const items = legs.map((leg, idx) => {
      const segment = Array.isArray(leg?.Segments) ? leg.Segments[0] || {} : {};

      const originName = (segment.Origin || "").split(",")[0] || "";
      const destinationName = (segment.Destination || "").split(",")[0] || "";

      const startDate =
        typeof segment.DepartureDate === "string"
          ? segment.DepartureDate.slice(0, 10)
          : "";
      const endDate =
        typeof segment.ArrivalDate === "string"
          ? segment.ArrivalDate.slice(0, 10)
          : "";

      const ticket_type = idx === 0 ? "outbound" : "inbound";

      return {
        item_id: `${originName}-${destinationName}`,
        item_name: "ferry",
        cross_product_promotion: "",
        affiliation: "turna.com",
        currency,
        item_brand: segment.OperatingCompany || null,
        index: idx + 1,
        coupon,
        discount: perLegDiscount,
        start_date: startDate,
        end_date: endDate,
        origin: originName,
        destination: destinationName,
        ticket_type,
        item_list_id: "ferry_search",
        item_list_name: "Ferry Search",
        item_category: "ferry",
        item_category2: isDomestic ? "domestic" : "international",
        item_category3: hasVehicle ? "with_car" : "without_car",
        item_category4: "economy",
        price: perLegPrice,
        quantity: 1,
      };
    });

    if (!items.length) return;

    const firstSeg =
      Array.isArray(legs[0]?.Segments) && legs[0].Segments.length > 0
        ? legs[0].Segments[0]
        : {};
    const lastLeg = legs[legs.length - 1];
    const lastSeg =
      Array.isArray(lastLeg?.Segments) && lastLeg.Segments.length > 0
        ? lastLeg.Segments[0]
        : {};

    const startDate =
      typeof firstSeg.DepartureDate === "string"
        ? dateFormats.iso(firstSeg.DepartureDate)
        : "";
    const endDate =
      typeof lastSeg.ArrivalDate === "string"
        ? dateFormats.iso(lastSeg.ArrivalDate)
        : "";

    const rtFlightId = items.map((it) => it.item_id).join(",");
    const tripType = legs.length > 1 ? "RT" : "OW";

    const basketId =
      data?.basketId ?? data?.BasketId ?? data?.reservationId ?? null;

    const tax = firstProduct?.PriceSummary?.Tax ?? 0;
    const scValue = firstProduct?.PriceSummary?.ServiceCharge ?? 0;

    const turnaPoint =
      data?.UserPointDetail?.AppliedPointAmount ??
      data?.UserPointDetail?.TotalUsablePointAmount ??
      null;
    const earnedTurnaPoint =
      data?.UserPointDetail?.EarnedPointAmount ??
      data?.EarnedPointAmount ??
      null;

    const payment =
      data?.AllPayments?.find((p) => p?.IsFinalized === true) ??
      data?.AllPayments?.[0];
    const paymentType =
      payment?.CardType === "CreditCard"
        ? "credit card"
        : payment?.CardType || "credit card";
    const creditCardType = payment?.CardOrganizationType;
    const creditCardBank = payment?.BankName;
    const paymentInstallment = payment?.InstallmentCount;

    // Önceki ecommerce datasını temizle
    pushToDataLayer({ ecommerce: null });

    pushToDataLayer(TrackingEvent.PURCHASE, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "purchase",
      trip_type: tripType,
      ...(rtFlightId && { rt_flight_id: rtFlightId }),
      ...(startDate &&
        endDate && { start_to_end_date: `${startDate} , ${endDate}` }),
      ecommerce: {
        transaction_id:
          basketId != null && String(basketId).trim() !== ""
            ? String(basketId)
            : undefined,
        value: totalValue,
        tax,
        currency,
        transaction_category: "ferry",
        payment_type: paymentType,
        ...(creditCardType && { credit_card_type: creditCardType }),
        ...(creditCardBank && { credit_card_bank: creditCardBank }),
        ...(paymentInstallment != null && {
          payment_installment: Number(paymentInstallment),
        }),
        sc_value: scValue,
        ...(coupon && { coupon }),
        coupon_used: coupon ? "yes" : "no",
        ...(basketId != null && String(basketId).trim() !== ""
          ? { basket_id: String(basketId) }
          : {}),
        ...(turnaPoint != null && { turna_point: Number(turnaPoint) }),
        ...(earnedTurnaPoint != null && {
          earned_turna_point: Number(earnedTurnaPoint),
        }),
        items,
      },
    });
  }

  trackShownError(data) {
    const errorMessage = data?.error_message || data?.message;
    if (!errorMessage) {
      return;
    }

    pushToDataLayer(TrackingEvent.SHOWN_ERROR, {
      event: "custom_event",
      event_category: "error",
      event_action: "shown_error",
      error_message: errorMessage,
      error_type: data?.error_type || "input validation",
      error_location: data?.error_location || "checkout",
    });
  }

  /**
   * select_coupon / view_coupon / coupon_applied
   */
  trackCouponEvent(event, data) {
    const offer = data.offer || {};
    const currencyCode = data.currencyCode || "TRY";

    const couponName = offer?.code || "";
    const couponValue = (() => {
      const raw = offer?.discountText;
      if (!raw) return "";
      const text = String(raw);
      const match = text.match(/(\d+(?:[.,]\d+)?)/);
      if (!match) return text;
      const num = Number(String(match[1]).replace(",", "."));
      return Number.isFinite(num) ? num : text;
    })();

    pushToDataLayer(event, {
      event: "custom_event",
      event_category: "coupon",
      event_action: event,
      coupon_name: couponName,
      coupon_value: null,
      coupon_currency: currencyCode,
      coupon_category: "ferry",
    });
  }

  trackGenericEvent(event, data) {
    pushToDataLayer(event, data);
  }
}
